// The cast players' full-screen controls (Shared / Player / Controls 4351:2706: Web Own cast 4351:2409 / 4351:2589,
// Cast viewer 4351:2455 / 4351:2635). One rule: only the viewer of someone else's cast differs.
import type { ReactNode } from "react";
import type { ControlButton, PlayerControlsSeek, PlayerControlsSpec } from "./controls/playerControlsSpec";

export interface CastControlsInput {
  /** Your own cast (host) or someone else's (viewer). */
  own: boolean;
  title: string;
  /** e.g. "Ana's link · 2 of 5". */
  meta?: string;
  exit: ControlButton;
  /** Host: stop (after a confirm); viewer: stop watching (Hide). */
  stop: ControlButton;
  prev?: ControlButton;
  playPause?: ControlButton;
  next?: ControlButton;
  /** The queue line + up-next toggle, under the top bar. */
  belowTop?: ReactNode;
  seek?: PlayerControlsSeek;
  embed?: PlayerControlsSpec["embed"];
}

/**
 * Host: exit · title · stop; previous · play/pause · next; the seek line as the player gives it.
 * Viewer: exit · title ("2 of 5") · stop watching; no previous / next / play / pause, the progress line read only
 * (the server timeline drives it), even when the queue has items.
 */
export function castControlsSpec(i: CastControlsInput): PlayerControlsSpec {
  const middle = i.own
    ? [i.prev, i.playPause && { ...i.playPause, variant: i.embed ? "default" : "large" as const }, i.next].filter((b): b is ControlButton => b != null)
    : [];
  return {
    title: i.title,
    meta: i.meta,
    top: [{ ...i.exit, start: true }, { ...i.stop, variant: "destructive" }],
    middle,
    belowTop: i.belowTop,
    seek: i.seek && (i.own ? i.seek : { ...i.seek, draggable: false }),
    embed: i.embed,
  };
}

/** What a cast player needs from its card to draw full screen (CastDockCard builds it). */
export interface CastFullScreen {
  /** The spec, given the player's own parts: its exit button, its progress line, its layout. */
  spec(player: { exit: ControlButton; seek?: PlayerControlsSeek; embed?: PlayerControlsSpec["embed"] }): PlayerControlsSpec;
  /** A dialog from a control (the stop confirm): drawn inside the full-screen element; the controls stay up. */
  dialog?: ReactNode;
  /** The player went in / out of full screen (the card drops its own controls meanwhile: one set of tags). */
  onChange?(on: boolean): void;
}
